import { type Ref, computed, onMounted, ref, unref, useAsync, useContext, useStore } from "@nuxtjs/composition-api";

import { useAmplitude } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import { handleErrorStatusCode, handleServiceError } from "@/utils/api";
import { NOT_FOUND } from "@/utils/enums/http-codes";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useLtkHashtagFeed
 *
 * @summary     A composable used to interact with the ltk post feed
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @param {string} hashtag - The category hashtag we will use to pull the ltk feed
 * @param {boolean} [isCategory=false] - Whether or not the feed is on a category page
 *
 * @returns {LtkFeedObject} Object for interacting with Ltk post feed
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Basic usage of `useLtkHashtagFeed` for category feed</caption>
 * ```ts
 * const { hashtag } = useCategory(params.hashtag);
 * // 2nd param tells us this is a category feed
 * const { onPaging, ltks, fetchState } = useLtkFeed(hashtag, true);
 * ```
 *
 * @example <caption>Usage of `useLtkHashtagFeed` for a non-category page</caption>
 * ```ts
 * const { hashtag } = useCategory(params.hashtag, false);
 * // 2nd param defaults to false
 * const { onPaging, ltks, fetchState } = useLtkFeed(hashtag);
 * ```
 *
 * @example <caption>Usage of `fetchState` for a LtkList</caption>
 * ```vue
 * // we need to destructure the ref since not all places using
 * // ltk-list are on composition.
 * <ltk-list
 *    :ltk-ids="ltks"
 *    :fetch-state="{ pending: fetchState.pending.value }"
 *    \@page="onPaging"
 * />
 * ```
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export const useLtkHashtagFeed = (hashtag: string, isCategory = false) => {
  const ctx = useContext();
  const pending = ref(true);
  const store = useStore();
  const amplitude = useAmplitude();

  const ltks = computed(() => getLtksStore(store).ltkFeed(`#${hashtag}`)?.ids ?? []);

  const fetch = async () => {
    const ltksStore = getLtksStore(store);
    const feed = `#${hashtag}`;
    // Skip server fetching, because data should already be loaded in async
    // But using fetchOnServer: false will render a spinner instead
    if (process.server) {
      return;
    }

    if (
      !ltksStore.hasFeedBeenFetched(feed) ||
      ltksStore.getActivityPostFeedHasMorePages(feed) ||
      ltksStore.getFeedHasMorePages(feed)
    ) {
      try {
        await ltksStore.fetchLtksByHashtag({ hashtagName: hashtag, overwrite: false });
        if (!ltksStore.hasFeedBeenFetched(feed)) {
          ctx.error({ statusCode: NOT_FOUND, message: `Error fetching ltks by hashtag (${hashtag})` });
        }
      } catch (err) {
        return handleServiceError({
          context: ctx,
          error: {
            status: handleErrorStatusCode(err),
            errorCode: "ltk-feed-not-found",
            developerMessage: `Error fetching hashtag (${hashtag})`,
          },
          originalError: err,
        });
      }
    }
  };

  useAsync(async () => {
    await fetch();
    pending.value = false;
  }, "ltkFeedFetch");

  const onPaging = async () => {
    pending.value = true;
    amplitude?.sendScrollEvent("/scroll/hashtag", ltks.value.length);
    await fetch();
    pending.value = false;
  };

  onMounted(() => {
    amplitude?.sendEvent("/page/hashtag-ltks", {
      hashtag,
      "is-category": isCategory,
    });
  });

  return {
    ltks,
    fetchState: { pending },
    onPaging,
  };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useLtk
 * @param    {Ref<string> | string} ltkId
 * @desc     A composable that returns an LTK from the ltks store
 *
 * @summary  Composable used to retrieve a single ltk.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useLtk = (ltkId: Ref<string> | string) => {
  const store = useStore();

  const ltk = computed(() => getLtksStore(store).ltks[unref(ltkId)]);

  return { ltk };
};
